import { Component, EventEmitter, forwardRef, Injector, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { CustomAttributeDefinition, CustomAttributeDefinitionInput, CustomAttributeSection, CustomAttributeSectionInput } from '../../entities/customAttributes/customAttributes';
import { MenuItem } from 'primeng/api';
import { CustomAttributesService } from '../../services/customAttributes/customAttributesService';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../services/toast/toast.service';
import { LoadingService } from '../../services/loading.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { CustomAttributeSectionDialogComponent } from '../custom-attribute-section-dialog/custom-attribute-section-dialog.component';
import { EditDialogService } from '../editDialog/editDialogService';
import { CustomAttributeDefinitionDialogComponent } from '../custom-attribute-definition-dialog/custom-attribute-definition-dialog.component';

@Component({
    selector: 'app-custom-attribute-definition',
    template: `
        <div
            class="flex justify-between items-center">
            <div class="flex-1 text-left">
                {{_customAttributeDefinition.label}}
            </div>
            <div class="flex-none text-right">
                <nx-default-button
                    [menuItems]="menuItems"
                    [menuWithIcon]="false"
                    class="pt-1"
                    [icon]="'icon-options'">
                </nx-default-button>
            </div>
        </div>
    `,
    styles: [`

    `]
})
export class CustomAttributeDefinitionComponent {

    dialogRef: DynamicDialogRef | undefined;

    public menuItems: MenuItem[] = [];
    public _customAttributeDefinition: CustomAttributeDefinition;
    public _customAttributeDefinitions: CustomAttributeDefinition[];

    @Input() set customAttributeDefinition(customAttributeDefinition: CustomAttributeDefinition) {
        this._customAttributeDefinition = customAttributeDefinition;
        this.setMenuItems();
    };

    @Input() set customAttributeDefinitions(customAttributeDefinitions: CustomAttributeDefinition[]) {
        this._customAttributeDefinitions = customAttributeDefinitions;
        this.setMenuItems();
    }

    @Output() refetch: EventEmitter<any> = new EventEmitter();

    constructor(
        private customAttributesService: CustomAttributesService,
        private confirmDialogService: ConfirmDialogService,
        private translate: TranslateService,
        private toastService: ToastService,
        private loadingService: LoadingService,
        public dialogService: EditDialogService
    ) {
    }

    setMenuItems() {
        this.menuItems = [];
        if (!this._customAttributeDefinition) {
            return;
        }
        this.menuItems.push({
            label: this.translate.instant('coreModuleModule.customAttributeDefinitionComponent.menu.update'),
            disabled: false,
            command: () => {
                this.updateCustomAttributeDefinition();
            }
        });
        this.menuItems.push({
            label: this.translate.instant('coreModuleModule.customAttributeDefinitionComponent.menu.delete'),
            disabled: !this.isLast(),
            command: () => {
                this.deleteCustomAttributeDefinition();
            }
        });

        this.menuItems.push({
            label: this.translate.instant('coreModuleModule.customAttributeDefinitionComponent.menu.moveUp'),
            disabled: this.isMoveUpDisabled(),
            command: () => {
                this.moveUp();
            }
        });

        this.menuItems.push({
            label: this.translate.instant('coreModuleModule.customAttributeDefinitionComponent.menu.moveDown'),
            disabled: this.isMoveDownDisabled(),
            command: () => {
                this.moveDown();
            }
        });
    }

    isLast(): boolean {
        if (!this._customAttributeDefinition || !this._customAttributeDefinitions) {
            return true;
        }
        return this._customAttributeDefinition.index === this._customAttributeDefinitions.length - 1;
    }

    isMoveUpDisabled(): boolean {
        if (!this._customAttributeDefinition) {
            return true;
        }
        return this._customAttributeDefinition.index === 0;
    }

    // move down is disabled if the current index is the last index in the list
    isMoveDownDisabled(): boolean {
        if (!this._customAttributeDefinition || !this._customAttributeDefinitions) {
            return true;
        }
        return !(this._customAttributeDefinition.index < this._customAttributeDefinitions.length - 1);
    }

    /**
     * moves the current custom attribute section up in the list of custom attribute sections.
     * This means: Swap the current section with the section that comes after it in the array.
     * @returns
     */
    moveDown(): void {
        if (!this._customAttributeDefinition || !this._customAttributeDefinitions) {
            return;
        }
        const currentIndex = this._customAttributeDefinition.index;
        const nextIndex = currentIndex + 1;
        // find the next section
        const nextDefinition = this._customAttributeDefinitions.find(definition => definition.index === nextIndex);
        if (!nextDefinition) {
            return;
        }
        // we must swap the indices by issuing an update mutation
        const currentDefinitionInput = new CustomAttributeDefinitionInput(this._customAttributeDefinition);
        currentDefinitionInput.index = nextIndex;
        const nextDefinitionInput = new CustomAttributeDefinitionInput(nextDefinition);
        nextDefinitionInput.index = currentIndex;
        this.loadingService.setLoading(true);
        this.customAttributesService.updateDefinition(currentDefinitionInput, () => {
            this.customAttributesService.updateDefinition(nextDefinitionInput, () => {
                this.refetch.emit();
                this.loadingService.setLoading(false);
                this.toastService.addToastWithMessage('coreModuleModule.customAttributeDefinitionComponent.customAttributeSectionMovedDownToastMessage');
            });
        });
    }

    /**
     * moves the current custom attribute section up in the list of custom attribute sections.
     * This means: Swap the current section with the section that comes before it in the array.
     * @returns
     */
    moveUp(): void {
        if (!this._customAttributeDefinition || !this._customAttributeDefinitions) {
            return;
        }
        const currentIndex = this._customAttributeDefinition.index;
        const previousIndex = currentIndex - 1;
        // find the previous section
        const previousDefinition = this._customAttributeDefinitions.find(definition => definition.index === previousIndex);
        if (!previousDefinition) {
            return;
        }
        // we must swap the indices by issuing an update mutation
        const currentDefinitionInput = new CustomAttributeDefinitionInput(this._customAttributeDefinition);
        currentDefinitionInput.index = previousIndex;
        const previousDefinitionInput = new CustomAttributeDefinitionInput(previousDefinition);
        previousDefinitionInput.index = currentIndex;
        this.loadingService.setLoading(true);
        this.customAttributesService.updateDefinition(currentDefinitionInput, () => {
            this.customAttributesService.updateDefinition(previousDefinitionInput, () => {
                this.refetch.emit();
                this.loadingService.setLoading(false);
                this.toastService.addToastWithMessage('coreModuleModule.customAttributeDefinitionComponent.customAttributeSectionMovedUpToastMessage');
            });
        });
    }

    deleteCustomAttributeDefinition(): void {
        this.dialogRef = this.confirmDialogService.open({
            message: this.translate.instant(
            'coreModuleModule.customAttributeDefinitionComponent.deleteCustomAttributeDefinitionDialogMessage',
            { customAttributeDefinitionId: this._customAttributeDefinition?.id }
            ),
            severity: ConfirmDialogSeverity.WARN,
            header: 'coreModuleModule.customAttributeDefinitionComponent.deleteCustomAttributeDefinitionDialogHeader',
        });
        this.dialogRef.onClose.subscribe((result: ConfirmDialogEventType) => {
            if (result === ConfirmDialogEventType.CONFIRM) {
            this.loadingService.setLoading(true);
            this.customAttributesService.deleteDefinition(this._customAttributeDefinition?.id, () => {
                this.refetch.emit();
                this.toastService.addToastWithMessage('coreModuleModule.customAttributeDefinitionComponent.customAttributeDefinitionDeletedToastMessage');
            });
            };
        })
    }

    updateCustomAttributeDefinition(): void {
        this.dialogRef = this.dialogService.open(CustomAttributeDefinitionDialogComponent, {
            header: 'coreModuleModule.customAttributeDefinitionComponent.updateCustomAttributeDefinitionDialogHeader',
            data: {
                customAttributeDefinition: this._customAttributeDefinition,
            }
        });
        this.dialogRef.onClose.subscribe((result) => {
            // result is true or false, indiciating if it should refetch
            if (result) {
                this.refetch.emit();
                this.toastService.addToastWithMessage('coreModuleModule.customAttributeDefinitionComponent.customAttributeDefinitionUpdatedToastMessage');
            }
        });
    }

}
